import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next'
import {
  Text,
  Flex,
  Box, Input,
} from '../../components/primitives'
import { useDynamicTokens } from '@reservoir0x/reservoir-kit-ui'
import { paths } from '@reservoir0x/reservoir-sdk'
import Layout from 'components/Layout'
import { useEffect, useRef, useState} from 'react'
import TokenCard from 'components/collections/TokenCard'
import { Grid } from 'components/primitives/Grid'
import { useIntersectionObserver } from 'usehooks-ts'
import fetcher from 'utils/fetcher'
import { useRouter } from 'next/router'

import { useDebounce } from 'usehooks-ts'
import LoadingCard from 'components/common/LoadingCard'
import { useMounted } from 'hooks'
import { NORMALIZE_ROYALTIES } from 'pages/_app'
import {faCog, faCube, faGlobe, faMagnifyingGlass, faSeedling,} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import supportedChains, { DefaultChain } from 'utils/chains'
import { Address, useAccount } from 'wagmi'
import {Head} from "../../components/Head";
import {MobileAttributeFilters} from "../../components/collections/filters/MobileAttributeFilters";
import {AttributeFilters} from "../../components/collections/filters/AttributeFilters";
import {FilterButton} from "../../components/common/FilterButton";
import LiveState from "../../components/common/LiveState";
import {SortTokens} from "../../components/collections/SortTokens";
import SelectedAttributes from "../../components/collections/filters/SelectedAttributes";
import {useMediaQuery} from "react-responsive";
import Img from "../../components/primitives/Img";
import optimizeImage from "../../utils/optimizeImage";
import {OpenSeaVerified} from "../../components/common/OpenSeaVerified";
import CopyText from "../../components/common/CopyText";
import {truncateAddress} from "../../utils/truncate";
import {getTokenAddressFromSymbol} from "@rcax/common";
import CryptoCurrencyIcon from 'components/primitives/CryptoCurrencyIcon'
import { TabsList, TabsTrigger, TabsContent } from 'components/primitives/Tab'
import * as Tabs from '@radix-ui/react-tabs'
import {MobileActivityFilters} from "../../components/common/MobileActivityFilters";
import {ActivityFilters} from "../../components/common/ActivityFilters";
import {CollectionActivityTable} from "../../components/collections/CollectionActivityTable";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>

const CollectionPage: NextPage<Props> = ({ ssr }) => {
  const router = useRouter()
  const { address } = useAccount()
  const [attributeFiltersOpen] = useState(false)
  const [activityFiltersOpen, setActivityFiltersOpen] = useState(true)
  const [activityTypes, setActivityTypes] = useState<ActivityTypes>([
    'sale',
    'mint',
  ])
  const [tokenSearchQuery, setTokenSearchQuery] = useState<string>('')
  const debouncedSearch = useDebounce(tokenSearchQuery, 500)
  const [initialTokenFallbackData, setInitialTokenFallbackData] = useState(true)
  const isMounted = useMounted()
  const isSmallDevice = useMediaQuery({ maxWidth: 905 }) && isMounted
  const [playingElement, setPlayingElement] = useState<
    HTMLAudioElement | HTMLVideoElement | null
  >()
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const loadMoreObserver = useIntersectionObserver(loadMoreRef, {})
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const token = router.query.token as string

  let tokenName = token.toUpperCase()

  let tokenAddress = getTokenAddressFromSymbol(token)

  if (!tokenAddress) {
    tokenName = 'RCAX'
    tokenAddress = getTokenAddressFromSymbol('RCAX')
  }

  let tokenQuery: Parameters<typeof useDynamicTokens>['0'] = {
    limit: 20,
    sortBy: 'floorAskPrice',
    sortDirection: 'asc',
    includeDynamicPricing: false,
    normalizeRoyalties: false,
    includeAttributes: true,
    ...(debouncedSearch.length > 0 && {
      tokenName: debouncedSearch,
    }),
    currencies: [tokenAddress],
  }

  const collectionSetId = process.env.NEXT_PUBLIC_COLLECTION_ID_PREMIUM

  if (collectionSetId) {
    tokenQuery.collectionsSetId = collectionSetId
  }

  const sortDirection = router.query['sortDirection']?.toString()
  const sortBy = router.query['sortBy']?.toString()

  if (sortBy === 'tokenId' || sortBy === 'rarity') tokenQuery.sortBy = sortBy
  if (sortDirection === 'desc') tokenQuery.sortDirection = 'desc'

  // Extract all queries of attribute type
  Object.keys({ ...router.query }).map((key) => {
    if (
      key.startsWith('attributes[') &&
      key.endsWith(']') &&
      router.query[key] !== ''
    ) {
      //@ts-ignore
      tokenQuery[key] = router.query[key]
    }
  })

  const {
    data: tokens,
    mutate,
    fetchNextPage,
    isFetchingInitialData,
    isFetchingPage,
    hasNextPage,
  } = useDynamicTokens(tokenQuery, {
    fallbackData: initialTokenFallbackData ? [ssr.tokens] : undefined,
  })

  useEffect(() => {
    const isVisible = !!loadMoreObserver?.isIntersecting
    if (isVisible) {
      fetchNextPage()
    }
  }, [loadMoreObserver?.isIntersecting])

  useEffect(() => {
    if (isMounted && initialTokenFallbackData) {
      setInitialTokenFallbackData(false)
    }
  }, [router.query])

  return (
    <Layout>
      <Head
        title={ `${tokenName} Listings` }
        description="Avatars listed for RCAX."
      />
      <Tabs.Root
        defaultValue="items"
        onValueChange={(value) => {
          if (value === 'items') {
          }
        }}
      >
        <Flex
          direction="column"
          css={{
            px: '$4',
            pt: '$4',
            pb: 0,
            '@md': {
              px: '$5',
            },

            '@xl': {
              px: '$6',
            },
          }}
        >
          <Flex
            justify="between"
            wrap="wrap"
            css={{ mb: '$4', gap: '$4' }}
            align="start"
          >
            <Flex
              direction="column"
              css={{
                gap: '$4',
                minWidth: 0,
                //flex: 1,
                width: '100%',
                '@lg': { width: 'unset' },
              }}
            >
              <Flex css={{ gap: '$4', flex: 1 }} align="center">
                <CryptoCurrencyIcon
                  css={{ height: 72, width: 72 }}
                  address={tokenAddress}
                  chainId={137}
                />
                <Box css={{ minWidth: 0 }}>
                  <Flex align="center" css={{ gap: '$1', mb: 0 }}>
                    <Text style="h4" as="h6" css={{ fontWeight: '600' }} ellipsify>
                      { `${tokenName} Listings` }
                    </Text>
                  </Flex>
                </Box>
              </Flex>
            </Flex>
          </Flex>

          <TabsList css={{ mt: 0 }}>
            <TabsTrigger value="items">Items</TabsTrigger>
          </TabsList>

          <TabsContent value="items">
            <Flex
              css={{
                gap: attributeFiltersOpen ? '$5' : '',
                position: 'relative',
              }}
              ref={scrollRef}
            >
              <Flex
                css={{
                  gap: attributeFiltersOpen ? '$5' : '',
                  position: 'relative',
                  width: '100%'
                }}
                ref={scrollRef}
              >
                {/*{isSmallDevice ? (*/}
                {/*  <MobileAttributeFilters*/}
                {/*    attributes={attributes}*/}
                {/*    scrollToTop={scrollToTop}*/}
                {/*  />*/}
                {/*) : (*/}
                {/*  <>*/}
                {/*    <AttributeFilters*/}
                {/*      attributes={attributes}*/}
                {/*      open={attributeFiltersOpen}*/}
                {/*      setOpen={setAttributeFiltersOpen}*/}
                {/*      scrollToTop={scrollToTop}*/}
                {/*    />*/}
                {/*  </>*/}
                {/*)}*/}
                <Box
                  css={{
                    flex: 1,
                    width: '100%',
                  }}
                >
                  <Flex css={{ marginBottom: '$4', gap: '$3' }} align="center">
                    <Flex align="center" css={{ gap: '$3', flex: 1 }}>
                      {/*{attributes &&*/}
                      {/*  attributes.length > 0 &&*/}
                      {/*  !isSmallDevice && (*/}
                      {/*    <FilterButton*/}
                      {/*      open={attributeFiltersOpen}*/}
                      {/*      setOpen={setAttributeFiltersOpen}*/}
                      {/*    />*/}
                      {/*  )}*/}
                      {!isSmallDevice && (
                        <Box
                          css={{ position: 'relative', flex: 1, maxWidth: 420 }}
                        >
                          <Box
                            css={{
                              position: 'absolute',
                              top: '50%',
                              left: '$4',
                              zIndex: 2,
                              transform: 'translate(0, -50%)',
                              color: '$gray11',
                            }}
                          >
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                          </Box>
                          <Input
                            css={{ pl: 42 }}
                            placeholder="Search by token name"
                            onChange={(e) => {
                              setTokenSearchQuery(e.target.value)
                            }}
                            value={tokenSearchQuery}
                          />
                        </Box>
                      )}
                    </Flex>
                    {/*{socketState !== null && <LiveState />}*/}
                    <Flex
                      justify={'end'}
                      css={{
                        width: '100%',
                        gap: '$2',
                        '@md': {
                          width: 'max-content',
                          gap: '$3',
                        },
                      }}
                    >
                      <SortTokens
                        css={{
                          order: 4,
                          px: '14px',
                          justifyContent: 'center',
                          '@md': {
                            order: 1,
                            width: '220px',
                            height: '48px',
                            minWidth: 'max-content',
                            px: '$5',
                          },
                        }}
                      />
                    </Flex>
                  </Flex>

                  {/*{!isSmallDevice && (*/}
                  {/*  <SelectedAttributes*/}
                  {/*    collection={collection}*/}
                  {/*    mutate={mutate}*/}
                  {/*  />*/}
                  {/*)}*/}
                  {/*<Flex*/}
                  {/*  css={{*/}
                  {/*    gap: '$4',*/}
                  {/*    mb: '$3',*/}
                  {/*    flexWrap: 'wrap',*/}
                  {/*    '@bp800': {*/}
                  {/*      display: 'flex',*/}
                  {/*    },*/}
                  {/*    display: 'flex',*/}
                  {/*  }}*/}
                  {/*>*/}
                  {/*  /!*<Flex css={{ gap: '$1' }}>*!/*/}
                  {/*  /!*  /!*<Text style="body1" as="p" color="subtle">*!/*!/*/}
                  {/*  /!*  /!*  Floor*!/*!/*/}
                  {/*  /!*  /!*</Text>*!/*!/*/}
                  {/*  /!*  /!*<Text style="body1" as="p" css={{ fontWeight: '700' }}>*!/*!/*/}
                  {/*  /!*  /!*  {nativePrice*!/*!/*/}
                  {/*  /!*  /!*    ? `${nativePrice?.toFixed(2)} ${chainCurrency.symbol}`*!/*!/*/}
                  {/*  /!*  /!*    : '-'}*!/*!/*/}
                  {/*  /!*  /!*</Text>*!/*!/*/}
                  {/*  /!*</Flex>*!/*/}
                  {/*  /!*<Flex css={{ gap: '$1' }}>*!/*/}
                  {/*  /!*  /!*<Text style="body1" as="p" color="subtle">*!/*!/*/}
                  {/*  /!*  /!*  Top Bid*!/*!/*/}
                  {/*  /!*  /!*</Text>*!/*!/*/}
                  {/*  /!*  /!*<Text style="body1" as="p" css={{ fontWeight: '700' }}>*!/*!/*/}
                  {/*  /!*  /!*  {topBidPrice*!/*!/*/}
                  {/*  /!*  /!*    ? `${topBidPrice?.toFixed(2) || 0} ${*!/*!/*/}
                  {/*  /!*  /!*      chainCurrency.symbol*!/*!/*/}
                  {/*  /!*  /!*    }`*!/*!/*/}
                  {/*  /!*  /!*    : '-'}*!/*!/*/}
                  {/*  /!*  /!*</Text>*!/*!/*/}
                  {/*  /!*</Flex>*!/*/}
                  {/*  /!*<Flex css={{ gap: '$1' }}>*!/*/}
                  {/*  /!*  /!*<Text style="body1" as="p" color="subtle">*!/*!/*/}
                  {/*  /!*  /!*  Count*!/*!/*/}
                  {/*  /!*  /!*</Text>*!/*!/*/}
                  {/*  /!*  /!*<Text style="body1" as="p" css={{ fontWeight: '700' }}>*!/*!/*/}
                  {/*  /!*  /!*  {Number(tokens?.length)?.toLocaleString()}*!/*!/*/}
                  {/*  /!*  /!*</Text>*!/*!/*/}
                  {/*  /!*</Flex>*!/*/}
                  {/*</Flex>*/}
                  <Grid
                    css={{
                      gap: '$4',
                      pb: '$6',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(160px, 1fr))',
                      '@md': {
                        gridTemplateColumns:
                          'repeat(auto-fill, minmax(160px, 1fr))',
                      },
                    }}
                  >
                    {isFetchingInitialData
                      ? Array(10)
                        .fill(null)
                        .map((_, index) => (
                          <LoadingCard key={`loading-card-${index}`} />
                        ))
                      : tokens.map((token, i) => (
                        <TokenCard
                          key={i}
                          token={token}
                          address={address as Address}
                          mutate={mutate}
                          onMediaPlayed={(e) => {
                            if (
                              playingElement &&
                              playingElement !== e.nativeEvent.target
                            ) {
                              playingElement.pause()
                            }
                            const element =
                              (e.nativeEvent.target as HTMLAudioElement) ||
                              (e.nativeEvent.target as HTMLVideoElement)
                            if (element) {
                              setPlayingElement(element)
                            }
                          }}
                          addToCartEnabled={
                            token.market?.floorAsk?.maker?.toLowerCase() !==
                            address?.toLowerCase()
                          }
                        />
                      ))}
                    <Box
                      ref={loadMoreRef}
                      css={{
                        display: isFetchingPage ? 'none' : 'block',
                      }}
                    >
                      {(hasNextPage || isFetchingPage) &&
                        !isFetchingInitialData && <LoadingCard />}
                    </Box>
                    {(hasNextPage || isFetchingPage) &&
                      !isFetchingInitialData && (
                        <>
                          {Array(6)
                            .fill(null)
                            .map((_, index) => (
                              <LoadingCard key={`loading-card-${index}`} />
                            ))}
                        </>
                      )}
                  </Grid>
                  {tokens.length == 0 && !isFetchingPage && (
                    <Flex
                      direction="column"
                      align="center"
                      css={{ py: '$6', gap: '$4' }}
                    >
                      <Text css={{ color: '$gray11' }}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} size="2xl" />
                      </Text>
                      <Text css={{ color: '$gray11' }}>No items found</Text>
                    </Flex>
                  )}
                </Box>
              </Flex>
            </Flex>
          </TabsContent>
        </Flex>
      </Tabs.Root>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps<{
  ssr: {
    tokens?: paths['/tokens/v6']['get']['responses']['200']['schema']
    hasAttributes: boolean
  }
}> = async ({ params, res }) => {
  const { reservoirBaseUrl } =
    supportedChains.find((chain) => params?.chain === chain.routePrefix) ||
    DefaultChain
  const headers: RequestInit = {
    headers: {
      'x-api-key': process.env.RESERVOIR_API_KEY || '',
    },
  }

  const token = params.token as string

  let tokenAddress = getTokenAddressFromSymbol(token)

  if (!tokenAddress) {
    tokenAddress = getTokenAddressFromSymbol('RCAX')
  }

  let tokensQuery: paths['/tokens/v6']['get']['parameters']['query'] = {
    sortBy: 'floorAskPrice',
    sortDirection: 'asc',
    limit: 20,
    includeDynamicPricing: false,
    normalizeRoyalties: false,
    includeAttributes: true,
    currencies: [tokenAddress],
  }

  const collectionSetId = process.env.NEXT_PUBLIC_COLLECTION_ID_PREMIUM

  if (collectionSetId) {
    tokensQuery.collectionsSetId = collectionSetId
  }

  const tokensPromise = fetcher(
    `${reservoirBaseUrl}/tokens/v6`,
    tokensQuery,
    headers
  )

  const promises = await Promise.allSettled([
    tokensPromise,
  ]).catch(() => {})
  const tokens: Props['ssr']['tokens'] =
    promises?.[0].status === 'fulfilled' && promises[0].value.data
      ? (promises[0].value.data as Props['ssr']['tokens'])
      : {}

  const hasAttributes =
    tokens?.tokens?.some(
      (token) => (token?.token?.attributes?.length || 0) > 0
    ) || false

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=30, stale-while-revalidate=60'
  )

  return {
    props: { ssr: { tokens, hasAttributes } },
  }
}

export default CollectionPage
